/**
 * graphLayout — the blueprint as a GRAPH, and where its nodes come to rest.
 *
 * Two pure steps, both DOM-free so they are unit-testable:
 *
 *   graphModel(blueprint)   nodes + edges, read off `components{}` and
 *                           `connections[]` and nothing else.
 *   settle(nodes, edges)    a tiny force simulation — Coulomb repulsion between
 *                           every pair, Hooke springs along the edges, mild
 *                           gravity toward the middle — run for a fixed number
 *                           of cooling steps and frozen. `id → {x, y}`.
 *
 * WHY TWO EDGE CLASSES. `connections[]` alone is not a picture of the widget:
 * on scene-studio 21 of 61 modules take part in a connection, so a graph drawn
 * from wires alone is a small wired core inside a ring of 39 unplaceable dots —
 * "orphan dust", which says nothing about a blueprint whose modules are in fact
 * tightly organised into pages. So containment (`children[]` and
 * `props.sections[].component`) is the SECOND class of edge: weak, long,
 * undirected, unlabelled. It pulls every module into its parent's lobe, so the
 * graph settles into one readable cluster per page with the wired core between
 * them, instead of a hairball plus dust.
 *
 * DETERMINISM is a requirement, not a nicety. `Math.random` is unavailable in
 * some contexts and would make both the first paint and the tests unstable, so
 * the seed positions come from a hash of the node id: the same blueprint always
 * settles to the same picture, and re-rendering never reshuffles it.
 *
 * The ceiling this is sized for is scene-studio: ~61 nodes / ~106 edges. A
 * naive O(n²) pass at that size is well under a frame, so there is no quadtree
 * and no dependency — the repo has no graph library and does not want one.
 */

/** `"shelves.itemActivated"` → `["shelves", "itemActivated"]`. */
function split(ref) {
  const s = String(ref || '');
  const i = s.indexOf('.');
  return i < 0 ? [s, ''] : [s.slice(0, i), s.slice(i + 1)];
}

/** The blueprint's wires, normalised. */
export function wiresOf(blueprint) {
  return (blueprint?.connections || [])
    .filter((c) => c?.from && c?.to)
    .map((c, i) => {
      const [fromId, signal] = split(c.from);
      const [toId, slot] = split(c.to);
      return {
        key: `${c.from}->${c.to}-${i}`,
        fromId, signal, toId, slot,
        mode: c.mode || 'async',
        executor: c.executor || 'ui',
        note: c.note || '',
      };
    });
}

/** A node's executor lane: what it SOURCES decides, the host is its own. */
export function laneOf(id, wires) {
  if (id === 'host') return 'host';
  const out = wires.filter((w) => w.fromId === id);
  if (!out.length) return 'ui';
  const n = { ui: 0, data: 0, host: 0 };
  for (const w of out) n[w.executor === 'data' ? 'data' : w.executor === 'host' ? 'host' : 'ui'] += 1;
  // Talking to the host does not make a component a host module; only an
  // explicit `data` executor moves it out of the UI lane.
  return n.data > n.ui && n.data >= n.host ? 'data' : 'ui';
}

/** The children a component OWNS — its `children[]` plus its page sections. */
function childrenOf(def) {
  const kids = [...(def?.children || [])];
  for (const s of def?.props?.sections || []) if (s?.component) kids.push(s.component);
  return kids;
}

/**
 * The graph itself: every component (plus a synthetic `host` when anything
 * talks to it) and the two classes of edge between them.
 *
 * A node carries what the renderer needs to draw it without re-reading the
 * blueprint — `type`/`variant` for the hover card, `lane` for the colour,
 * `degree` for the size.
 */
export function graphModel(blueprint) {
  const comps = blueprint?.components || {};
  const wires = wiresOf(blueprint);
  const hasHost = wires.some((w) => w.fromId === 'host' || w.toId === 'host');
  const ids = [...Object.keys(comps), ...(hasHost ? ['host'] : [])];
  const known = new Set(ids);

  const edges = [];
  for (const w of wires) {
    if (!known.has(w.fromId) || !known.has(w.toId)) continue;
    edges.push({ ...w, kind: 'connection', label: `${w.signal} → ${w.slot}` });
  }
  const drawn = new Set();
  for (const id of Object.keys(comps)) {
    for (const kid of childrenOf(comps[id])) {
      const key = `contain:${id}->${kid}`;
      if (!comps[kid] || kid === id || drawn.has(key)) continue;
      drawn.add(key);
      edges.push({
        key, kind: 'containment', fromId: id, toId: kid,
        signal: '', slot: '', mode: 'sync', executor: 'ui', note: '', label: '',
      });
    }
  }

  const degree = new Map(ids.map((id) => [id, 0]));
  for (const e of edges) {
    if (e.fromId === e.toId) continue;
    degree.set(e.fromId, (degree.get(e.fromId) || 0) + 1);
    degree.set(e.toId, (degree.get(e.toId) || 0) + 1);
  }

  const nodes = ids.map((id) => ({
    id,
    type: id === 'host' ? 'host' : (comps[id]?.type || '?'),
    variant: comps[id]?.variant || null,
    lane: laneOf(id, wires),
    degree: degree.get(id) || 0,
  }));

  return { nodes, edges };
}

/** FNV-1a — a stable 32-bit hash, so a node's seed position is its own. */
function hash32(s) {
  let h = 2166136261;
  for (let i = 0; i < String(s).length; i += 1) {
    h ^= String(s).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** The simulation's constants — one place, overridable per call. */
export const FORCE = {
  steps: 340,
  // Coulomb constant (f = k / d²), tuned on the real fleet: this is what puts
  // ~56px between the closest pair of scene-studio's 61 nodes and keeps the
  // whole canvas around 700px, i.e. readable without panning.
  repulsion: 12000,
  gravity: 0.018, // pull toward the origin, so nothing drifts away
  cooling: 0.992, // per-step temperature decay
  maxStep: 22, // px a node may move in one step, before cooling
  connection: { k: 0.055, rest: 128 }, // strong + short: a wire means "these belong together"
  containment: { k: 0.018, rest: 210 }, // weak + long: a lobe, not a chain
};

/**
 * Settle the graph. PURE and DETERMINISTIC: same nodes + edges in, same
 * positions out, with no `Math.random` and no DOM anywhere in it.
 *
 * Returns `{ [id]: { x, y } }`, translated so the smallest x and y are 0 — the
 * renderer adds its own padding for the node radius and its label.
 */
export function settle(nodes, edges, opts = {}) {
  const o = { ...FORCE, ...opts };
  const n = nodes.length;
  const out = {};
  if (!n) return out;

  const index = new Map(nodes.map((nd, i) => [nd.id, i]));
  const x = new Float64Array(n);
  const y = new Float64Array(n);
  const fx = new Float64Array(n);
  const fy = new Float64Array(n);

  // SEED: a golden-angle spiral whose angle is the id's hash. Deterministic,
  // evenly spread, and no two nodes start on the same point (which would make
  // the repulsion step divide by zero).
  for (let i = 0; i < n; i += 1) {
    const h = hash32(nodes[i].id);
    const a = ((h % 10007) / 10007) * Math.PI * 2;
    const r = 30 + Math.sqrt(i + 1) * 34 + (h % 23);
    x[i] = Math.cos(a) * r;
    y[i] = Math.sin(a) * r;
  }

  const springs = edges
    .map((e) => ({ a: index.get(e.fromId), b: index.get(e.toId), c: e.kind === 'containment' ? o.containment : o.connection }))
    .filter((s) => s.a !== undefined && s.b !== undefined && s.a !== s.b);

  let temp = 1;
  for (let step = 0; step < o.steps; step += 1) {
    fx.fill(0);
    fy.fill(0);
    for (let i = 0; i < n; i += 1) {
      for (let j = i + 1; j < n; j += 1) {
        let dx = x[i] - x[j];
        let dy = y[i] - y[j];
        let d2 = dx * dx + dy * dy;
        // Two nodes exactly on top of each other have no direction to push
        // apart in; nudge them onto a deterministic diagonal instead.
        if (d2 < 0.01) { dx = 0.1; dy = 0.1; d2 = 0.02; }
        const d = Math.sqrt(d2);
        const f = o.repulsion / d2;
        fx[i] += (dx / d) * f; fy[i] += (dy / d) * f;
        fx[j] -= (dx / d) * f; fy[j] -= (dy / d) * f;
      }
    }
    for (const s of springs) {
      const dx = x[s.b] - x[s.a];
      const dy = y[s.b] - y[s.a];
      const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const f = s.c.k * (d - s.c.rest);
      fx[s.a] += (dx / d) * f; fy[s.a] += (dy / d) * f;
      fx[s.b] -= (dx / d) * f; fy[s.b] -= (dy / d) * f;
    }
    for (let i = 0; i < n; i += 1) {
      fx[i] -= x[i] * o.gravity;
      fy[i] -= y[i] * o.gravity;
      const cap = o.maxStep * temp;
      x[i] += Math.max(-cap, Math.min(cap, fx[i]));
      y[i] += Math.max(-cap, Math.min(cap, fy[i]));
    }
    temp *= o.cooling;
  }

  let minX = Infinity;
  let minY = Infinity;
  for (let i = 0; i < n; i += 1) {
    if (x[i] < minX) minX = x[i];
    if (y[i] < minY) minY = y[i];
  }
  for (let i = 0; i < n; i += 1) {
    // Rounded to a tenth: the picture is unchanged and the numbers stay
    // readable in a snapshot or a failing assertion.
    out[nodes[i].id] = {
      x: Math.round((x[i] - minX) * 10) / 10,
      y: Math.round((y[i] - minY) * 10) / 10,
    };
  }
  return out;
}

export default graphModel;
